import { getSchema, type JSONContent } from '@tiptap/core';
import { DOMParser, DOMSerializer, type Node, type Schema } from '@tiptap/pm/model';
import { prepareBaseEditorExtensions } from '@WysiwygEditor/components/Editor/extensions/base-extensions';

import { PDF_EXTENSION } from '../constants';
import type { ImportFailure } from '../types';

import { fitToSchema, type Repairs } from './write/fit-to-schema';
import { toClauseDoc } from './write/to-clause-doc';
import { runPasses } from './passes';

export interface ClauseFileImportResult {
  doc: JSONContent;
  repairs: Repairs;
  warnings: string[];
}

export class ClauseFileImportError extends Error {
  name = 'ClauseFileImportError';

  constructor(
    readonly failure: ImportFailure,
    cause?: unknown,
  ) {
    super(cause instanceof Error ? `${failure} (${cause.name})` : failure);
  }
}

// the editor collapses whitespace when it parses saved HTML, so the import states it collapsed already
const asEditorParses = (schema: Schema, doc: Node) => {
  const container = document.createElement('div');
  container.append(DOMSerializer.fromSchema(schema).serializeFragment(doc.content));
  return DOMParser.fromSchema(schema).parse(container);
};

const readWordFile = async (bytes: ArrayBuffer) => {
  const { readDocx } = await import('./read-docx/read-docx').catch((cause: unknown) => {
    throw new ClauseFileImportError('importFailed', cause);
  });
  try {
    return await readDocx(bytes);
  } catch (cause) {
    throw new ClauseFileImportError('unreadableWord', cause);
  }
};

const readPdfFile = async (bytes: ArrayBuffer, signal?: AbortSignal) => {
  const { readPdf } = await import('./read-pdf/read-pdf').catch((cause: unknown) => {
    throw new ClauseFileImportError('importFailed', cause);
  });
  try {
    return await readPdf(bytes, signal);
  } catch (cause) {
    if (signal?.aborted) throw cause;
    throw new ClauseFileImportError(
      cause instanceof Error && cause.name === 'PasswordException' ? 'protectedPdf' : 'corrupt',
      cause,
    );
  }
};

export const importClauseFile = async (
  bytes: ArrayBuffer,
  { fileName, signal }: { fileName: string; signal?: AbortSignal },
): Promise<ClauseFileImportResult> => {
  signal?.throwIfAborted();
  const read = fileName.toLowerCase().endsWith(PDF_EXTENSION)
    ? await readPdfFile(bytes, signal)
    : await readWordFile(bytes);
  signal?.throwIfAborted();

  const draft = runPasses(read);
  const schema = getSchema(prepareBaseEditorExtensions([]));
  const { doc: fitted, repairs } = fitToSchema(schema, toClauseDoc(draft));
  const doc = asEditorParses(schema, fitted);
  if (!doc.textContent.trim()) {
    throw new ClauseFileImportError('corrupt');
  }

  return { doc: doc.toJSON(), repairs, warnings: draft.warnings };
};
